import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Timer, X } from "lucide-react";
import api from "../lib/api";

/**
 * Horizontal strip of 1-3 upsell suggestions rendered on any
 * checkout-like screen. Fetches from `/api/upsell/suggestions` with the
 * page context + current cart total, renders as tap-cards with a live
 * countdown so the user feels the flash-promo urgency.
 *
 * Props:
 *   context  : "table_order" | "shop_cart" | "event_booking" | "table_reservation"
 *   cartTotal: current cart amount in euros (drives eligibility filters)
 *   limit    : 1-5, default 3
 *   compact  : dense layout for small drawers
 */
const euro = (n) => `${Number(n || 0).toFixed(2)} €`;
const DISMISS_KEY = "sn_upsell_dismissed"; // localStorage key: comma-separated promo ids

export default function UpsellStrip({ context = "table_order", cartTotal = 0, limit = 3, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set((localStorage.getItem(DISMISS_KEY) || "").split(",").filter(Boolean));
    } catch { return new Set(); }
  });
  const nav = useNavigate();

  // Refresh on context/cart change but not on every render — coarse
  // rounding on cartTotal so a 0.10€ tweak doesn't refetch endlessly.
  const bucket = Math.floor(cartTotal / 5);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/upsell/suggestions?context=${context}&order_total=${cartTotal}&limit=${limit}`)
      .then(({ data }) => { if (!cancel) setItems(data || []); })
      .catch(() => { if (!cancel) setItems([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, bucket, limit]);

  // 1s ticker for the countdown badges.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const visible = useMemo(
    () => items.filter((it) => !dismissed.has(it.id)),
    [items, dismissed],
  );

  if (loading || visible.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, Array.from(next).join(",")); } catch {}
  };

  const click = (s) => {
    // Fire-and-forget analytics; the promo code is already in the URL.
    try { window.dispatchEvent(new CustomEvent("sn:upsell_click", { detail: { id: s.id, kind: s.kind } })); } catch {}
    const sep = s.target_url.includes("?") ? "&" : "?";
    nav(`${s.target_url}${sep}promo=${s.promo_code}`);
  };

  return (
    <div className="w-full" data-testid={`upsell-strip-${context}`}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-medium">
          Suggestions flash
        </div>
      </div>
      <div className={`grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"}`}>
        {visible.slice(0, limit).map((s) => {
          const secsLeft = Math.max(0, Math.floor(s._expires_at_ms ? (s._expires_at_ms - now) / 1000 : s.expires_in_sec));
          const mins = Math.floor(secsLeft / 60);
          const secs = secsLeft % 60;
          return (
            <div
              key={s.id}
              className="relative rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-card p-4 transition hover:border-primary/60 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_-8px_rgba(244,212,147,0.35)]"
              data-testid={`upsell-card-${s.id}`}
            >
              <button
                onClick={() => dismiss(s.id)}
                className="absolute top-2 right-2 w-6 h-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-background/60 flex items-center justify-center"
                aria-label="Ignorer cette suggestion"
                data-testid={`upsell-dismiss-${s.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-center justify-between gap-2 mb-1.5">
                {s.badge && (
                  <span className="text-[9px] uppercase tracking-[0.18em] bg-primary/20 border border-primary/40 rounded-full px-2 py-0.5 text-primary">
                    {s.badge}
                  </span>
                )}
                <span className="ml-auto text-[9px] uppercase tracking-[0.15em] flex items-center gap-1 text-amber-300">
                  <Timer className="w-3 h-3" /> {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                </span>
              </div>

              <div className="font-display text-base leading-tight mb-1 pr-6">{s.title}</div>
              <div className="text-xs text-muted-foreground leading-snug mb-3 min-h-[2.2em]">{s.subtitle}</div>

              {s.discounted_price != null && s.original_price != null && (
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-primary font-medium">{euro(s.discounted_price)}</span>
                  <span className="text-xs text-muted-foreground line-through">{euro(s.original_price)}</span>
                  <span className="text-[10px] bg-primary/10 border border-primary/30 rounded-full px-1.5 py-0.5 text-primary">
                    -{s.discount_pct}%
                  </span>
                </div>
              )}

              <button
                onClick={() => click(s)}
                className="w-full h-10 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium flex items-center justify-center gap-1.5 transition"
                data-testid={`upsell-cta-${s.id}`}
              >
                {s.cta} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
